import React from 'react';
import { Navigate } from 'react-router-dom';

import once from 'lodash/once';

import { BaseUser } from '@common/react/objects/BaseUser';
import { useApplicationContext } from '@common/react/components/Core/Application/Application';
import ItemProviderWithStore from '@common/react/components/Core/ItemProviderWithStore/ItemProviderWithStore';
import { BaseRoutesPropsMap } from '@common/typescript/objects/BaseRoutesPropsMap';

/**
 *
 * use for simple role
 * const canRoleAccess = (route: RoutesPropsMap, role: UserRole) =>
 *    !route.roles || (route.roles.length > 0 && route.roles.includes(role));
 *
 * const baseCanAccess: CanAccessDelegate<User, RoutesPropsMap> = (user, route) =>
 *    !route || (user && canRoleAccess(route, user.role));
 *
 */
export type CanAccessDelegate<
	TUser extends BaseUser,
	TRoutesPropsMap extends BaseRoutesPropsMap
> = (user: TUser, route: TRoutesPropsMap) => boolean;

export interface BaseSecureRouteWrapperProps<
	TUser extends BaseUser,
	TRoutesPropsMap extends BaseRoutesPropsMap
> {
	path: string;
	loadRequest?: string;
	storageName?: string;
	canAccess?: CanAccessDelegate<TUser, TRoutesPropsMap>;
	redirectPath?: string;
	children: React.ReactNode | ((context: BaseSecureRoutesProviderContext) => React.ReactNode);
}

export interface BaseSecureRoutesProviderContext {
	canAccess: (path: string) => boolean;
}

const createBaseSecureRoutesProviderContext = once(
	() => React.createContext({} as BaseSecureRoutesProviderContext),
);

export const useBaseSecureRoutesProviderContext: () => BaseSecureRoutesProviderContext = () =>
	React.useContext(createBaseSecureRoutesProviderContext());

export const BaseSecureRouteWrapper = <
	TUser extends BaseUser,
	TRoutesPropsMap extends BaseRoutesPropsMap
>(props: BaseSecureRouteWrapperProps<TUser, TRoutesPropsMap>) => {
	const SecureRoutesProviderContext = createBaseSecureRoutesProviderContext();

	const {
		path,
		loadRequest = 'routesPropsPagesMap',
		storageName = 'routesPropsPagesMap',
		children,
		redirectPath,
		canAccess: propsCanAccess = (user, route) => Boolean(!route || user),
	} = props;
	const { getUser } = useApplicationContext();
	const user = getUser<TUser>();

	if (!user && redirectPath) {
		return <Navigate to={redirectPath} />;
	}

	return (
		<ItemProviderWithStore
			storageName={storageName}
			id={-1}
			loadRequest={loadRequest}
			skipInitLoad
		>
			{({ state: { item } }) => {
				const context = React.useMemo(() => ({
					canAccess: (path: string = ''): boolean => {
						const slashIndex = (path).lastIndexOf('/');

						const mainPath = path.substring(1, slashIndex > 0 ? slashIndex : path.length);
						const route = item?.[mainPath];

						return propsCanAccess(user, route);
					},
				}), []);

				return <SecureRoutesProviderContext.Provider value={context}>
					{context.canAccess(path)
						? (typeof children === 'function' ? children(context) : children)
						: <Navigate to="/access-denied" />}
				</SecureRoutesProviderContext.Provider>;
			}}
		</ItemProviderWithStore>
	);
};

export default BaseSecureRouteWrapper;
